import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { CONTENT_KIND_SLUG, detailHref } from "@/lib/insights";

export interface LatestInsight {
  title: string;
  summary: string;
  href: string;
  kindLabel: string;
}

const KIND_LABEL: Record<string, string> = {
  blog: "Article",
  webinar: "Webinar",
  podcast: "Podcast",
  evidence: "Evidence",
  news: "News",
  workflow: "Workflow",
  whitepaper: "Whitepaper",
  case_study: "Case study",
};

export function useLatestInsight(options: { kinds?: string[] } = {}) {
  const kinds = options.kinds;
  return useQuery({
    queryKey: ["latest-insight-promo", kinds?.join(",") ?? "any"],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<LatestInsight | null> => {
      let q = supabase
        .from("insights_public")
        .select("title, summary, slug, kind, published_at, listed")
        .eq("status", "published")
        // Unlisted whitepapers (listed=false) are hidden from the mega-menu
        // callout; listed whitepapers appear like normal insights.
        .or("kind.neq.whitepaper,listed.eq.true");
      if (kinds && kinds.length) q = q.in("kind", kinds as any);
      const { data, error } = await q
        .order("published_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      const kind = (data.kind ?? "blog") as keyof typeof CONTENT_KIND_SLUG;
      return {
        title: data.title,
        summary: data.summary ?? "",
        href: detailHref({ kind, slug: data.slug }),
        kindLabel: KIND_LABEL[kind] ?? "Latest",
      };
    },
  });
}
